import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Ported from @curbnturf/image-processing (unchanged) so importing it doesn't
 * pull in that lib's other, @curbnturf/network/site-coupled components via
 * its shared barrel — see photo-editor.component.ts's header comment.
 *
 * R15/A — Resolves the "get me a photo" step in a platform-appropriate way:
 *  - On iOS/Android (Capacitor): shows the native action sheet (Camera |
 *    Photo Library) via @capacitor/camera and returns the picked image as a
 *    Blob ready for the PhotoEditorComponent.
 *  - On web: returns null; caller falls back to a standard <input type="file">.
 *
 * Callers should:
 *  1. Try `pickNative()` — if it returns a Blob, feed it straight to the
 *     PhotoEditorComponent.
 *  2. If it returns null, open the file input UI they already have.
 */
@Injectable({ providedIn: 'root' })
export class PhotoPickerService {
  private platformId = inject<object>(PLATFORM_ID);

  /** True when running inside a Capacitor native shell (iOS or Android). */
  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Prompt the native Camera / Photo Library action sheet. Returns null when
   *  not on a native platform so the caller can fall back to file input. */
  async pickNative(opts: { quality?: number } = {}): Promise<Blob | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    if (!this.isNative) return null;
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,       // "Camera | Photos" action sheet
        quality: opts.quality ?? 90,
        allowEditing: false,               // we do our own editing
        correctOrientation: true,
      });
      if (!photo.webPath) return null;
      const res = await fetch(photo.webPath);
      return await res.blob();
    } catch {
      // User cancelled the picker, permission denied, etc. — treat as no-op.
      return null;
    }
  }
}
